/**
 * useFirebaseAutoSave - Hook for automatic tournament saving to Firestore
 * Saves tournament state to Firebase when changes occur (debounced)
 */
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import firebaseSyncService from '../services/firebaseSync';

const useFirebaseAutoSave = (tournamentState, enabled = true) => {
  const { isSignedIn, getCurrentUser } = useAuth();
  const { showError } = useToast();
  const timeoutRef = useRef(null);
  const previousStateRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState(null);

  useEffect(() => {
    // Only auto-save if user is signed in and auto-save is enabled
    if (!enabled || !isSignedIn() || !firebaseSyncService.currentTournamentId) {
      return;
    }

    const user = getCurrentUser();
    if (!user) return;

    // Debounce: wait 2 seconds after last change before saving
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Check if state actually changed (avoid unnecessary saves)
    const currentStateStr = JSON.stringify(tournamentState);
    if (previousStateRef.current === currentStateStr) {
      return;
    }

    previousStateRef.current = currentStateStr;

    timeoutRef.current = setTimeout(async () => {
      setIsSaving(true);
      try {
        // Check if tournament needs to be created first
        if (!firebaseSyncService.currentTournamentId) {
          console.log('🆕 No tournament ID found, creating new tournament...');
          const tournamentId = await firebaseSyncService.createNewTournament(
            tournamentState,
            user.email,
            null // Auto-generate name
          );
          if (tournamentId) {
            console.log('✅ New tournament created:', tournamentId);
          }
        } else {
          // Tournament already exists, just save
          await firebaseSyncService.saveTournament(
            tournamentState,
            user.email,
            'auto-save'
          );
          console.log('💾 Auto-saved tournament to Firestore');
        }
        setLastSaveTime(new Date());
        // Don't show toast for auto-save to avoid spamming
      } catch (error) {
        console.error('❌ Auto-save error:', error);
        showError('Erreur de sauvegarde automatique');
      } finally {
        setIsSaving(false);
      }
    }, 2000); // 2 second debounce

    // Cleanup timeout on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [tournamentState, enabled, isSignedIn, getCurrentUser, showError]);

  return { isSaving, lastSaveTime };
};

export default useFirebaseAutoSave;
